/**
 * Kiosk audio slots: every kiosk screen, registration step, cash payment status
 * and modal that can have a spoken clip. Keys are stable — they are stored in
 * `kiosk_audio_clips.slot_key` and name the admin screenshots
 * (`bb-admin-fe-react/public/react/kiosk-audio-slots/<key>.png`). Never rename a key
 * that has clips; add a new one instead.
 *
 * One global set of clips, no per-shop overrides.
 */

export const kioskAudioLanguages = ["hu", "en", "de", "sk"] as const;
export type KioskAudioLanguage = (typeof kioskAudioLanguages)[number];

/**
 * `page` slots belong to a screen (or a step/status inside one) and replace each
 * other on navigation. `modal` slots interrupt the page clip only when they have
 * a clip of their own.
 */
export type KioskAudioSlotKind = "page" | "modal";

export type KioskAudioSlotGroup = {
  key: string;
  /** Hungarian label for the admin page. */
  label: string;
};

export type KioskAudioSlotDefinition = {
  key: string;
  /** Hungarian label for the admin page. */
  label: string;
  group: string;
  kind: KioskAudioSlotKind;
};

/** Admin page order: the parent page each slot is shown under. */
export const kioskAudioSlotGroups = [
  { key: "global", label: "Globális" },
  { key: "start", label: "Kezdőképernyő" },
  { key: "login", label: "Bejelentkezés" },
  { key: "registration", label: "Regisztráció" },
  { key: "profile", label: "Profil" },
  { key: "machines", label: "Gépválasztás" },
  { key: "payment", label: "Fizetés" },
  { key: "cash-payment", label: "Készpénzes fizetés" },
  { key: "balance", label: "Egyenlegfeltöltés" },
  { key: "coupons", label: "Kuponok" },
  { key: "reservations", label: "Foglalások" },
  { key: "invoice", label: "Számla" },
  { key: "finish", label: "Befejezés" },
] as const satisfies readonly KioskAudioSlotGroup[];

export type KioskAudioSlotGroupKey = (typeof kioskAudioSlotGroups)[number]["key"];

export const kioskAudioSlots = [
  // Global: modals shared across screens
  { key: "modal.help", label: "Segítség", group: "global", kind: "modal" },
  { key: "modal.tech-support", label: "Technikai támogatás", group: "global", kind: "modal" },
  { key: "modal.inactivity", label: "Inaktivitás", group: "global", kind: "modal" },
  { key: "modal.offline", label: "Offline módban nem elérhető", group: "global", kind: "modal" },
  { key: "modal.error", label: "Hibaüzenet (Hoppá!)", group: "global", kind: "modal" },
  { key: "modal.logout-coupon-warning", label: "Kijelentkezés érvényesített kuponnal", group: "global", kind: "modal" },
  { key: "modal.popup", label: "Reklám popup", group: "global", kind: "modal" },
  { key: "modal.contract-document", label: "Szerződés megtekintése", group: "global", kind: "modal" },
  { key: "modal.printer-error", label: "Nyomtató hiba", group: "global", kind: "modal" },
  { key: "modal.we-are-closed", label: "Zárva vagyunk", group: "global", kind: "modal" },
  { key: "modal.temporarily-closed", label: "Ideiglenesen zárva", group: "global", kind: "modal" },

  // Start
  { key: "page.start-screen", label: "Kezdőképernyő", group: "start", kind: "page" },

  // Login
  { key: "page.login-option-selector", label: "Van már fiókod?", group: "login", kind: "page" },
  { key: "modal.terms", label: "Szerződési feltételek elfogadása (vendég)", group: "login", kind: "modal" },
  { key: "page.login", label: "Bejelentkezés", group: "login", kind: "page" },
  { key: "modal.contracts-update", label: "Módosult szerződési feltételek", group: "login", kind: "modal" },
  { key: "modal.user-not-found", label: "Érvénytelen bejelentkezési adatok", group: "login", kind: "modal" },
  { key: "modal.resend-confirmation-success", label: "Aktiváló email újraküldve", group: "login", kind: "modal" },
  { key: "page.password-reminder", label: "Elfelejtett jelszó", group: "login", kind: "page" },
  { key: "modal.password-reminder-sent", label: "Jelszó emlékeztető elküldve", group: "login", kind: "modal" },

  // Registration
  { key: "page.registration.personal-details", label: "Személyes adatok", group: "registration", kind: "page" },
  { key: "page.registration.email-verification", label: "Email megerősítés", group: "registration", kind: "page" },
  { key: "page.registration.billing-info", label: "Számlázási adatok", group: "registration", kind: "page" },
  { key: "page.registration.auth-and-contracts", label: "Belépési adatok és szerződések", group: "registration", kind: "page" },
  { key: "modal.username-recommendation", label: "Ajánlott felhasználónév", group: "registration", kind: "modal" },
  { key: "modal.marketing-confirm", label: "Biztosan nem kérsz ajánlatokat?", group: "registration", kind: "modal" },
  { key: "modal.registration-success", label: "Sikeres regisztráció", group: "registration", kind: "modal" },

  // Profile
  { key: "page.profile-edit", label: "Adataim", group: "profile", kind: "page" },
  { key: "modal.missing-profile-data", label: "Hiányzó profiladatok", group: "profile", kind: "modal" },
  { key: "modal.missing-profile-data.coupon-success", label: "Hiányzó adatok: kuponkód", group: "profile", kind: "modal" },
  { key: "modal.missing-profile-data.saved", label: "Hiányzó adatok: mentve", group: "profile", kind: "modal" },
  { key: "page.welcome-coupon.hungarian-phone", label: "Üdvözlő kupon: magyar telefonszám?", group: "profile", kind: "page" },
  { key: "page.welcome-coupon", label: "Üdvözlő kupon: telefonszám igazolás", group: "profile", kind: "page" },
  { key: "page.welcome-coupon.verified", label: "Üdvözlő kupon: sikeres igazolás", group: "profile", kind: "page" },

  // Machines
  { key: "page.machine-type-selector", label: "Mosás vagy szárítás", group: "machines", kind: "page" },
  { key: "page.machine-selector.washing", label: "Mosógép választása", group: "machines", kind: "page" },
  { key: "page.machine-selector.drying", label: "Szárítógép választása", group: "machines", kind: "page" },
  { key: "modal.machine-selector.washer", label: "Fontos! (mosógép)", group: "machines", kind: "modal" },
  { key: "modal.machine-selector.dryer", label: "Fontos! (szárítógép)", group: "machines", kind: "modal" },
  { key: "modal.closing-warning", label: "Hamarosan zárunk", group: "machines", kind: "modal" },
  { key: "page.dryer-bundle-selector", label: "Szárító program választása", group: "machines", kind: "page" },
  { key: "modal.dryer-bundle-info", label: "Szárító program információ", group: "machines", kind: "modal" },

  // Payment
  { key: "page.payment-selector", label: "Fizetési mód választása", group: "payment", kind: "page" },
  { key: "modal.cash-info", label: "Készpénzes fizetés figyelmeztetés", group: "payment", kind: "modal" },
  { key: "page.payment-card", label: "Bankkártyás fizetés", group: "payment", kind: "page" },
  { key: "page.payment-balance", label: "Fizetés egyenlegből", group: "payment", kind: "page" },

  // Cash payment (v5 cash screen, statuses from features/payment/cash-payment-status.ts)
  { key: "page.cash-payment", label: "Kérjük fizess!", group: "cash-payment", kind: "page" },
  { key: "page.cash-payment.finishing", label: "Fizetés befejezése / visszajáró", group: "cash-payment", kind: "page" },
  { key: "page.cash-payment.balance-credit", label: "Visszajáró jóváírása", group: "cash-payment", kind: "page" },
  { key: "modal.cash-payment.success", label: "Sikeres fizetés", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.timed-out", label: "Fizetés időtúllépés", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.aborted", label: "Fizetés megszakadt", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.hardware-error", label: "Hardver hiba", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.server-error", label: "Szerver hiba", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.success-payout-failed", label: "Sikeres fizetés, visszajáró hiba", group: "cash-payment", kind: "modal" },
  { key: "modal.cash-payment.aborted-payout-failed", label: "Megszakított fizetés, visszajáró hiba", group: "cash-payment", kind: "modal" },

  // Balance top-up
  { key: "modal.balance-topup-warning", label: "Egyenlegfeltöltés figyelmeztetés", group: "balance", kind: "modal" },
  { key: "page.balance-replenish-cash", label: "Egyenlegfeltöltés készpénzzel", group: "balance", kind: "page" },
  { key: "page.balance-replenish-cash.finishing", label: "Egyenlegfeltöltés: kérlek várj", group: "balance", kind: "page" },
  { key: "modal.balance-topup.success-payout-failed", label: "Sikeres feltöltés, visszajáró hiba", group: "balance", kind: "modal" },
  { key: "modal.balance-topup.aborted-payout-failed", label: "Megszakított feltöltés, visszajáró hiba", group: "balance", kind: "modal" },

  // Coupons
  { key: "page.coupon", label: "Kupon beváltása", group: "coupons", kind: "page" },
  { key: "modal.coupon-valid", label: "Érvényes kuponkód", group: "coupons", kind: "modal" },

  // Reservations
  { key: "page.active-reservations", label: "Aktív foglalások", group: "reservations", kind: "page" },

  // Invoice
  { key: "page.invoice-question", label: "Kérsz számlát?", group: "invoice", kind: "page" },
  { key: "page.invoice-generation", label: "Számlázási adatok", group: "invoice", kind: "page" },
  { key: "modal.invoice-sent", label: "Számla elküldve", group: "invoice", kind: "modal" },
  { key: "modal.invoice-error", label: "Számla generálási hiba", group: "invoice", kind: "modal" },

  // Finish
  { key: "page.bubbles-loves-you", label: "Köszönjük!", group: "finish", kind: "page" },
  { key: "page.wash-start-guide", label: "Mosógép indítása (útmutató)", group: "finish", kind: "page" },
  { key: "page.dry-start-temperature", label: "Szárítógép: hőfok beállítása", group: "finish", kind: "page" },
  { key: "page.wash-start-success", label: "Mosógép elindult", group: "finish", kind: "page" },
  { key: "page.wash-start-failed", label: "Mosógép nem indult el", group: "finish", kind: "page" },
  { key: "modal.collectors-discount", label: "Gyűjtögetős kedvezmény", group: "finish", kind: "modal" },
  { key: "modal.activate-registration", label: "Bubbles fiók aktiválása", group: "finish", kind: "modal" },
  { key: "modal.ask-new-transaction", label: "Másik gép indítása?", group: "finish", kind: "modal" },
] as const satisfies readonly (KioskAudioSlotDefinition & { group: KioskAudioSlotGroupKey })[];

export type KioskAudioSlotKey = (typeof kioskAudioSlots)[number]["key"];

const slotsByKey = new Map<string, (typeof kioskAudioSlots)[number]>(kioskAudioSlots.map((slot) => [slot.key, slot]));

export function isKioskAudioSlotKey(key: string): key is KioskAudioSlotKey {
  return slotsByKey.has(key);
}

export function isKioskAudioLanguage(language: string): language is KioskAudioLanguage {
  return (kioskAudioLanguages as readonly string[]).includes(language);
}

export function getKioskAudioSlot(key: KioskAudioSlotKey) {
  return slotsByKey.get(key)!;
}

/**
 * Screenshot URL in the admin app. Its static files are served under /react/,
 * so the PNGs live in `bb-admin-fe-react/public/react/kiosk-audio-slots/`.
 */
export function kioskAudioSlotScreenshotPath(key: KioskAudioSlotKey) {
  return `/react/kiosk-audio-slots/${key}.png`;
}
